import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { components } from '@cedarreachai/api-client';
import {
  createWorkspace,
  getSession,
  getWorkspace,
  listWorkspaces,
  updateWorkspaceAutomation,
} from '@/lib/api/endpoints';
import { useActionKey } from '@/lib/api/actionKey';
import { sessionKeys, workspaceKeys } from '@/lib/queryKeys';

type Schemas = components['schemas'];

export type Workspace = Schemas['Workspace'];
export type Session = Schemas['Session'];
export type Membership = Schemas['Membership'];
export type AutomationLevel = Schemas['AutomationLevel'];

/** In order of increasing autonomy (design doc §4.3). */
export const AUTOMATION_LEVELS: readonly AutomationLevel[] = [
  'advise',
  'draft',
  'queue',
  'execute',
  'paid_execute',
];

export function useSession() {
  return useQuery({
    queryKey: sessionKeys.me(),
    queryFn: ({ signal }) => getSession(signal),
  });
}

export function useWorkspaceList() {
  return useQuery({
    queryKey: sessionKeys.workspaces(),
    queryFn: ({ signal }) => listWorkspaces(signal),
  });
}

/** The workspace record: name, locale, timezone, kill-switch state, automation ceiling. */
export function useWorkspaceDetail(workspaceId: string) {
  return useQuery({
    queryKey: workspaceKeys.workspace(workspaceId),
    queryFn: ({ signal }) => getWorkspace(workspaceId, signal),
  });
}

/** The caller's membership in one workspace, from `/me`. `undefined` while loading. */
export function useMembership(workspaceId: string): {
  readonly membership: Membership | null | undefined;
  readonly session: ReturnType<typeof useSession>;
} {
  const session = useSession();
  const membership = session.data
    ? (session.data.memberships.find((m) => m.workspace_id === workspaceId) ?? null)
    : undefined;
  return { membership, session };
}

/** One key per user action: a retry of the same form never creates a second workspace. */
export function useCreateWorkspace() {
  const client = useQueryClient();
  const key = useActionKey();
  return useMutation({
    mutationFn: (input: Schemas['CreateWorkspaceRequest']) =>
      createWorkspace(input, key.keyFor({ input })),
    onSuccess: () => {
      key.settle();
      void client.invalidateQueries({ queryKey: sessionKeys.workspaces() });
      void client.invalidateQueries({ queryKey: sessionKeys.me() });
    },
  });
}

/** `PATCH workspaces/{id}` with `If-Match: <version>`; a stale version is `VERSION_CONFLICT`. */
export function useUpdateAutomation(workspaceId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { readonly level: AutomationLevel; readonly version: number }) =>
      updateWorkspaceAutomation(workspaceId, input.level, input.version),
    onSuccess: (updated) => {
      client.setQueryData(workspaceKeys.workspace(workspaceId), updated);
      void client.invalidateQueries({ queryKey: sessionKeys.me() });
    },
  });
}
